import React, { useRef, useState } from 'react';
import WechatIcon from 'js/design_system/icons/wechat.svg';
import RedditIcon from 'js/design_system/icons/reddit.svg';
import WhatsappIcon from 'js/design_system/icons/whatsapp.svg';
import { Button } from 'js/design_system/Button';
import { ButtonLink } from 'js/design_system/link/ButtonLink';
import FacebookIcon from 'js/design_system/icons/facebook.svg';
import TwitterIcon from 'js/design_system/icons/x_twitter.svg';
import LinkedinIcon from 'js/design_system/icons/linkedin.svg';
import BlueskyIcon from 'js/design_system/icons/bluesky.svg';
import trackClick from 'js/utils/trackClick';
import { useShareImpressions } from 'js/journals/useShareImpressions';
import {
  articleShareUrl,
  ArticleShareOption,
} from 'js/journals/articleShareUrl';
import { buildShareText, ShareAuthor, ShareVariant } from './shareText';

interface ShareButtonsProps {
  submission: {
    id: string | number;
    journal_name: string;
    title: string;
    authors?: readonly ShareAuthor[] | null;
    share_summary?: string | null;
  };
  paperUrl: string;
  marginTop?: number | string;
  source: string;
  articleUrl?: string;
  onWechatShare?: () => void;
  wechatExpanded?: boolean;
  /** Defaults to the published paper page wording. */
  variant?: ShareVariant;
}

export const ShareButtons: React.FC<ShareButtonsProps> = (props) => {
  const [copySuccess, setCopySuccess] = useState(false);
  const buttonsRef = useRef<HTMLDivElement>(null);
  const clickTrackSuffix = props.paperUrl.includes('#podcast')
    ? '_podcast'
    : '';
  useShareImpressions(
    buttonsRef,
    props.source,
    props.submission.id,
    clickTrackSuffix,
  );

  const shareUrl = (option: ArticleShareOption): string =>
    props.articleUrl
      ? articleShareUrl(props.articleUrl, option)
      : props.paperUrl;
  const clipboardUrl = shareUrl('clipboard');

  const shareParams = {
    journalName: props.submission.journal_name,
    title: props.submission.title,
    authors: props.submission.authors,
    summary: props.submission.share_summary,
    variant: props.variant,
  };
  const blueskyString = encodeURIComponent(
    buildShareText(
      { ...shareParams, paperUrl: shareUrl('bluesky') },
      'bluesky',
    ),
  );
  const linkedInString = encodeURIComponent(
    buildShareText(
      { ...shareParams, paperUrl: shareUrl('linkedin') },
      'linkedin',
    ),
  );
  const twitterString = encodeURIComponent(
    buildShareText(
      { ...shareParams, paperUrl: shareUrl('twitter') },
      'twitter',
    ),
  );

  const trackShareClick = (clickType: string) => {
    trackClick({
      source: `${props.source}${clickType}${clickTrackSuffix}`,
      entityId: props.submission.id,
    });
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(clipboardUrl);
      trackShareClick('clipboard');
      setCopySuccess(true);

      // Reset to original state after 2 seconds
      setTimeout(() => {
        setCopySuccess(false);
      }, 2000);
    } catch (_error) {
      // If clipboard API fails, fallback to the old method
      const textArea = document.createElement('textarea');
      textArea.value = clipboardUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);

      trackShareClick('clipboard');
      setCopySuccess(true);

      setTimeout(() => {
        setCopySuccess(false);
      }, 2000);
    }
  };

  return (
    <div
      ref={buttonsRef}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '16px',
        marginTop: props.marginTop ?? 32,
      }}
    >
      <Button
        size={'sm'}
        secondary
        icon={copySuccess ? 'check' : 'attachment'}
        data-share-channel="clipboard"
        onClick={handleCopyLink}
      >
        {copySuccess ? 'Copied' : 'Copy link'}
      </Button>
      <ButtonLink
        size={'sm'}
        icon={<BlueskyIcon />}
        aria-label={'Bluesky'}
        data-share-channel="bluesky"
        secondary
        href={`https://bsky.app/intent/compose?text=${blueskyString}`}
        onClick={() => {
          trackShareClick('bluesky');
        }}
        target={'_blank'}
      />
      <ButtonLink
        secondary
        size={'sm'}
        icon={<FacebookIcon />}
        aria-label={'Facebook'}
        data-share-channel="facebook"
        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl('facebook'))}`}
        onClick={() => {
          trackShareClick('facebook');
        }}
        target={'_blank'}
      />
      {/* LinkedIn doesn't support the query params for some reason*/}
      <ButtonLink
        aria-label={'LinkedIn'}
        data-share-channel="linkedin"
        size={'sm'}
        icon={<LinkedinIcon />}
        secondary
        href={`https://www.linkedin.com/shareArticle?text=${linkedInString}`}
        onClick={() => {
          trackShareClick('linkedin');
        }}
        target={'_blank'}
      />
      <ButtonLink
        size={'sm'}
        icon={<TwitterIcon />}
        aria-label={'X / Twitter'}
        data-share-channel="twitter"
        secondary
        href={`https://twitter.com/intent/tweet?text=${twitterString}`}
        onClick={() => {
          trackShareClick('twitter');
        }}
        target={'_blank'}
      />
      <ButtonLink
        size="sm"
        secondary
        icon={<RedditIcon />}
        aria-label="Reddit"
        data-share-channel="reddit"
        href={`https://www.reddit.com/submit?url=${encodeURIComponent(shareUrl('reddit'))}&title=${encodeURIComponent(props.submission.title)}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackShareClick('reddit')}
      />
      {props.articleUrl && (
        <ButtonLink
          size="sm"
          secondary
          icon={<WhatsappIcon />}
          aria-label="WhatsApp"
          data-share-channel="whatsapp"
          href={`https://wa.me/?text=${encodeURIComponent(`${props.submission.title}\n\n${shareUrl('whatsapp')}`)}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackShareClick('whatsapp')}
        />
      )}
      {props.onWechatShare && (
        <Button
          size="sm"
          secondary
          icon={<WechatIcon />}
          aria-label="WeChat"
          data-share-channel="wechat"
          aria-expanded={props.wechatExpanded}
          onClick={() => {
            if (!props.wechatExpanded) trackShareClick('wechat');
            props.onWechatShare?.();
          }}
        />
      )}
    </div>
  );
};
